import React from 'react'

import styles from './_card.module.scss'
import {
  Button,
  Checkbox,
  Icon,
  type IconStringList,
  Image,
  Skeleton,
  Tag,
  type TagProps,
  type TooltipProps,
  TrimText,
} from '../../module'
import {
  type CheckedItemsType,
  type HandleCheckType,
} from './CardContainer/CardContainer'

type FileProps = {
  /** Unique name for the file */
  name: string
  /** Optional prop that determines the checked state for the file */
  isChecked?: boolean
  /** Optional prop that represents the thumbnail image URL of the file, enabling visual identification or content preview. */
  url?: string
  /** Optional prop that will hold the information related to the file */
  data?: string[]
}

export type CardProps<FileGeneric extends FileProps> = {
  /** The file that is displayed in the card */
  fileProps: FileGeneric
  /** Optional prop that accepts an function that will make a callout when the file checkbox state is changed */
  onCheckCallout?: (name: string, check: boolean) => void
  /** Function that will make a callout when the card is clicked */
  cardCallout: () => void
  /** Optional prop that accepts an array of tag objects which include the text, color */
  tags?: TagProps[]
  /** Optional prop that accepts children elements defined in this file -IconButton ,TextButton, ButtonContainer and VerticalDivider */
  footerChildren?: React.ReactNode
  /** Optional prop to define width of the card */
  width?: number
  /** Optional prop that accepts a function that will make a callout when the checkbox is clicked */
  handleCheck?: HandleCheckType
  /** Optional prop that accepts an index for the card */
  index?: number
  /** Optional prop that accepts an object of checked items */
  checkedItems?: CheckedItemsType
  /** Optional prop that accepts a boolean to determine if the card is loading */
  isLoading?: boolean
  /** Optional prop to add a test id to the Card for QA testing */
  qaTestId?: string
}

const Divider = (): React.JSX.Element => {
  return <div className={styles.divider} />
}

const Card = <FileGeneric extends FileProps>({
  fileProps,
  cardCallout,
  onCheckCallout,
  tags,
  footerChildren,
  width,
  handleCheck,
  index,
  checkedItems,
  isLoading,
  qaTestId = 'card',
}: CardProps<FileGeneric>): React.JSX.Element => {
  return (
    <div
      className={styles.cardContainer}
      style={{ width }}
      qa-test-id={qaTestId}
    >
      {isLoading ? null : onCheckCallout ? (
        <Card.CheckboxClickArea
          fileProps={fileProps}
          onCheckCallout={onCheckCallout}
          handleCheck={handleCheck}
          checkedItems={checkedItems}
          index={index}
        >
          <Checkbox
            hideLabel
            label=''
            stateName={fileProps.name}
            name={fileProps.name}
            checked={
              handleCheck && checkedItems
                ? checkedItems?.[index ?? -1]
                : fileProps.isChecked
            }
            callout={(name, check) => {
              onCheckCallout(name?.toString() ?? '', check)
              handleCheck?.(
                index ?? -1,
                event as unknown as React.MouseEvent<HTMLButtonElement>,
                check,
              )
            }}
            customClass={styles.checkbox}
            handleCheck={handleCheck}
            index={index}
          />
        </Card.CheckboxClickArea>
      ) : null}
      <Button
        as='unstyled'
        onClick={cardCallout}
        className={styles.button}
        disabled={isLoading}
      >
        <div className={styles.fileContainer}>
          {isLoading ? (
            <Skeleton.Card />
          ) : (
            <div className={styles.fileImageContainer}>
              <Image
                url={fileProps.url}
                alt={fileProps.name}
                style={{ width: '100%', height: '100%', marginRight: 0 }} // override default styles from Image component
                iconSize='80px'
                imgStyle={{
                  width: 'auto',
                  height: 'auto',
                }}
              />
            </div>
          )}
          <Divider />
          <div className={styles.fileInfo}>
            {isLoading ? (
              <Skeleton.CardFooter />
            ) : (
              <>
                <TrimText text={fileProps.name} customClass={styles.trimText} />
                {fileProps.data &&
                  fileProps.data.map((d, index) => (
                    <span key={index}>{d}</span>
                  ))}
                {tags && (
                  <div className={styles.tagContainer}>
                    {tags?.map((tag, i) => (
                      <Tag key={i} {...tag} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Button>
      {footerChildren ? (
        <>
          <Divider />
          {isLoading ? (
            <div className={styles.cardFooterLoader}>
              <Skeleton.CardFooter />
            </div>
          ) : (
            footerChildren
          )}
        </>
      ) : null}
    </div>
  )
}

const IconButton = ({
  icon,
  onClick,
  tooltipProps,
}: {
  icon: IconStringList
  onClick: () => void
  tooltipProps: Omit<TooltipProps, 'children'>
}) => {
  return (
    <div className={styles.footerButton}>
      <Button
        as='unstyled'
        onClick={onClick}
        className={styles.actionButton}
        tooltip={tooltipProps}
      >
        <Icon icon={icon} className={styles.iconColor} iconSize='20px' />
      </Button>
    </div>
  )
}

const TextButton = ({
  onClick,
  tooltipProps,
  text,
}: {
  onClick: () => void
  tooltipProps: Omit<TooltipProps, 'children'>
  text: string
}) => {
  return (
    <div className={styles.footerButton}>
      <Button
        as='unstyled'
        onClick={onClick}
        className={styles.actionButton}
        tooltip={tooltipProps}
      >
        {text}
      </Button>
    </div>
  )
}

const ButtonContainer = ({ children }: { children: React.ReactNode }) => {
  return <div className={styles.buttonContainer}>{children}</div>
}

const VerticalDivider = () => {
  return <div className={styles.verticalDivider} />
}

// New component for expanded checkbox click area
const CheckboxClickArea = ({
  children,
  fileProps,
  onCheckCallout,
  handleCheck,
  checkedItems,
  index,
}: {
  children: React.ReactNode
  fileProps: { name: string; isChecked?: boolean }
  onCheckCallout: (name: string, check: boolean) => void
  handleCheck?: HandleCheckType
  checkedItems?: CheckedItemsType
  index?: number
}) => {
  return (
    <Button
      as='unstyled'
      className={styles.checkboxClickArea}
      onClick={(e) => {
        e.stopPropagation()
        onCheckCallout(
          fileProps.name?.toString() ?? '',
          !(handleCheck && checkedItems
            ? checkedItems?.[index ?? -1]
            : fileProps.isChecked),
        )
        handleCheck?.(
          index ?? -1,
          e as unknown as React.MouseEvent<HTMLButtonElement>,
          !(checkedItems ? checkedItems?.[index ?? -1] : fileProps.isChecked),
        )
      }}
    >
      {children}
    </Button>
  )
}

Card.IconButton = IconButton
Card.TextButton = TextButton
Card.ButtonContainer = ButtonContainer
Card.VerticalDivider = VerticalDivider
Card.CheckboxClickArea = CheckboxClickArea
export { Card }
